/**
 * Rich embeds: the cards bots attach to their messages (title, description, fields, colour, images, footer).
 * People's link previews are embeds too, made by the server; see `LinkEmbed` and `RichEmbed` in types.ts.
 */
import { z } from 'zod';

export const EMBED_LIMITS = {
  perMessage: 10,
  title: 256,
  description: 4096,
  fields: 25,
  fieldName: 256,
  fieldValue: 1024,
  author: 256,
  footer: 2048,
  /** All the text in one message's embeds together. */
  total: 6000,
} as const;

/**
 * Embed images point at files attached to the same message, as `attachment://<filename>` (or the attachment's
 * id). Spawnwire only loads images from itself, so nobody's client contacts another server to show an embed.
 */
export const ATTACHMENT_REF_PREFIX = 'attachment://';

const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .regex(/^https?:\/\/[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i, 'Links must be full http:// or https:// addresses');

const imageRef = z.object({
  url: z
    .string()
    .trim()
    .max(300)
    .refine((u) => u.startsWith(ATTACHMENT_REF_PREFIX) && u.length > ATTACHMENT_REF_PREFIX.length, 'Embed images must be files attached to the same message: use attachment://<filename>'),
});

export const embedFieldSchema = z.object({
  name: z.string().trim().min(1, 'Fields need a name').max(EMBED_LIMITS.fieldName),
  value: z.string().trim().min(1, 'Fields need a value').max(EMBED_LIMITS.fieldValue),
  inline: z.boolean().default(false),
});

export const embedSchema = z
  .object({
    title: z.string().trim().min(1).max(EMBED_LIMITS.title).optional(),
    description: z.string().trim().min(1).max(EMBED_LIMITS.description).optional(),
    /** Makes the title a link. */
    url: httpUrl.optional(),
    /** The bar down the left side, as 0xRRGGBB. */
    color: z.number().int().min(0).max(0xffffff).optional(),
    author: z.object({ name: z.string().trim().min(1).max(EMBED_LIMITS.author), url: httpUrl.optional() }).optional(),
    fields: z.array(embedFieldSchema).max(EMBED_LIMITS.fields).default([]),
    image: imageRef.optional(),
    thumbnail: imageRef.optional(),
    footer: z.object({ text: z.string().trim().min(1).max(EMBED_LIMITS.footer) }).optional(),
    timestamp: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((e) => e.title || e.description || e.fields.length || e.image || e.thumbnail || e.author || e.footer, 'An embed needs a title, description, field, image, author or footer');

export type EmbedInput = z.input<typeof embedSchema>;
/** An embed after validation, with defaults filled in. */
export type EmbedData = z.output<typeof embedSchema>;

/** How much text an embed holds, counted toward EMBED_LIMITS.total. */
export function embedTextLength(e: z.output<typeof embedSchema>): number {
  return (
    (e.title?.length ?? 0) +
    (e.description?.length ?? 0) +
    (e.author?.name.length ?? 0) +
    (e.footer?.text.length ?? 0) +
    e.fields.reduce((n, f) => n + f.name.length + f.value.length, 0)
  );
}

export const embedListSchema = z
  .array(embedSchema)
  .max(EMBED_LIMITS.perMessage, `A message can have at most ${EMBED_LIMITS.perMessage} embeds`)
  .refine((list) => list.reduce((n, e) => n + embedTextLength(e), 0) <= EMBED_LIMITS.total, `Embeds can hold at most ${EMBED_LIMITS.total} characters of text together`);

/** The file name (or id) an `attachment://` reference names. */
export function attachmentRefName(url: string): string {
  return url.slice(ATTACHMENT_REF_PREFIX.length);
}
